import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaLock,
  FaCookie,
  FaDatabase,
  FaEnvelope,
  FaFileAlt,
} from "react-icons/fa";
import { useFooter } from "../context/FooterContext";

const PrivacyPolicy = () => {
  const { setShowFooter } = useFooter();
  const [isDarkMode, setIsDarkMode] = React.useState(false);

  useEffect(() => {
    setShowFooter(true);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getBgColor = () => (isDarkMode ? "#0a0a0f" : "#f5f7fb");

  const sections = [
    {
      title: "Information We Collect",
      icon: <FaDatabase size={20} />,
      content: [
        "Personal Information: Name, email address, USN, branch, semester, profile picture",
        "Academic Information: Notes uploaded, SGPA/CGPA calculations, result history",
        "Usage Data: Pages visited, features used, time spent on platform",
        "Communication: Messages sent in community chat, contact form submissions",
        "Technical Data: IP address, browser type, device information, access times",
      ],
    },
    {
      title: "How We Use Your Information",
      icon: <FaLock size={20} />,
      content: [
        "To provide and maintain our services",
        "To authenticate your identity and secure your account",
        "To improve and personalize your experience",
        "To calculate and display academic rankings",
        "To communicate important updates and notifications",
        "To moderate community chat and prevent abuse",
        "To analyze usage patterns and improve features",
      ],
    },
    {
      title: "Data Storage & Security",
      icon: <FaShieldAlt size={20} />,
      content: [
        "Your data is stored securely on MongoDB Atlas with encryption at rest",
        "Passwords are hashed using bcrypt before storage",
        "All communications are encrypted using HTTPS/TLS",
        "Cloudinary is used for secure file storage with access controls",
        "We implement rate limiting to prevent abuse",
        "Regular security audits and updates are performed",
      ],
    },
    {
      title: "Cookies & Tracking",
      icon: <FaCookie size={20} />,
      content: [
        "We use cookies to maintain your login session",
        "Local storage is used to save your preferences (dark mode, pinned messages)",
        "No third-party tracking cookies are used",
        "You can disable cookies in your browser settings",
        "Session data is automatically cleared upon logout",
      ],
    },
    {
      title: "Information Sharing",
      icon: <FaFileAlt size={20} />,
      content: [
        "We do not sell your personal information to third parties",
        "Academic rankings are publicly visible within the VTUGenX platform",
        "Chat messages are visible to all authenticated users",
        "Notes you upload become visible to all users after approval",
        "We may share data with law enforcement if required by law",
      ],
    },
    {
      title: "Your Rights",
      icon: <FaEnvelope size={20} />,
      content: [
        "Access your personal data through your profile page",
        "Update or correct your information at any time",
        "Request deletion of your account and associated data",
        "Opt out of promotional communications",
        "Download your data in JSON format (coming soon)",
        "Contact us for any privacy concerns",
      ],
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: getBgColor(),
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: "center", marginBottom: "3rem" }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              width: "70px",
              height: "70px",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem auto",
            }}
          >
            <FaShieldAlt size={32} color="white" />
          </div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: getTextColor(),
              marginBottom: "0.5rem",
            }}
          >
            Privacy <span style={{ color: "#8b5cf6" }}>Policy</span>
          </h1>
          <p style={{ color: getTextSecondary() }}>
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
          <p
            style={{
              color: getTextSecondary(),
              marginTop: "0.5rem",
              maxWidth: "700px",
              margin: "0.5rem auto 0",
            }}
          >
            Your privacy is important to us. This policy explains how we
            collect, use, and protect your information.
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "2rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <p style={{ color: getTextColor(), lineHeight: "1.6" }}>
            VTUGenX ("we", "our", "us") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you use our website and services. By
            using VTUGenX, you consent to the data practices described in this
            policy.
          </p>
        </motion.div>

        {/* Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            style={{
              background: getCardBackground(),
              borderRadius: "20px",
              padding: "1.5rem",
              marginBottom: "1.5rem",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1rem",
                paddingBottom: "0.75rem",
                borderBottom: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: "rgba(139,92,246,0.1)",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#8b5cf6",
                }}
              >
                {section.icon}
              </div>
              <h2
                style={{
                  fontSize: "1.3rem",
                  fontWeight: "600",
                  color: getTextColor(),
                  margin: 0,
                }}
              >
                {section.title}
              </h2>
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                color: getTextSecondary(),
                lineHeight: "1.8",
              }}
            >
              {section.content.map((item, idx) => (
                <li key={idx} style={{ marginBottom: "0.5rem" }}>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}

        {/* Data Retention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <h2
            style={{
              fontSize: "1.3rem",
              fontWeight: "600",
              color: getTextColor(),
              marginBottom: "1rem",
            }}
          >
            Data Retention
          </h2>
          <p
            style={{
              color: getTextSecondary(),
              lineHeight: "1.6",
              marginBottom: "1rem",
            }}
          >
            We retain your personal information only for as long as necessary to
            provide you with our services and as described in this Privacy
            Policy.
          </p>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.5rem",
              color: getTextSecondary(),
              lineHeight: "1.8",
            }}
          >
            <li>Account information: Retained until you request deletion</li>
            <li>Chat messages: Retained for moderation purposes</li>
            <li>
              Uploaded notes: Retained indefinitely or until removed by
              moderators
            </li>
            <li>
              Result data: Cached for 7 days, then re-fetched from VTU servers
            </li>
            <li>Files in chat: Automatically deleted after 30 days</li>
          </ul>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <h2
            style={{
              fontSize: "1.3rem",
              fontWeight: "600",
              color: getTextColor(),
              marginBottom: "1rem",
            }}
          >
            Children's Privacy
          </h2>
          <p style={{ color: getTextSecondary(), lineHeight: "1.6" }}>
            VTUGenX is intended for college students. We do not knowingly
            collect personal information from children under 13. If you believe
            we have collected information from a child under 13, please contact
            us immediately.
          </p>
        </motion.div>

        {/* Changes to Policy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "2rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <h2
            style={{
              fontSize: "1.3rem",
              fontWeight: "600",
              color: getTextColor(),
              marginBottom: "1rem",
            }}
          >
            Changes to This Policy
          </h2>
          <p
            style={{
              color: getTextSecondary(),
              lineHeight: "1.6",
              marginBottom: "1rem",
            }}
          >
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last Updated" date.
          </p>
          <p style={{ color: getTextSecondary(), lineHeight: "1.6" }}>
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </p>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          style={{
            textAlign: "center",
            padding: "2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            borderRadius: "20px",
            color: "white",
          }}
        >
          <h3 style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>
            Questions?
          </h3>
          <p style={{ opacity: 0.9, marginBottom: "1rem" }}>
            If you have any questions about this Privacy Policy, please contact
            us:
          </p>
          <p style={{ marginBottom: "0.5rem" }}>
            <strong>vtugenx@gmail.com</strong>
          </p>
          <p>Bengaluru, Karnataka, India - 590018</p>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
